/**
 * PackingPreviewThumbnails — what the packer actually looks at.
 *
 * A packing checklist row used to show one catalogue thumbnail, which for a
 * custom garment is a blank shirt and for a front-and-back garment is one
 * image standing in for two prints. This shows the real thing instead: the
 * customer's own preview for a custom item, or a labelled tile per side that
 * has a garment mockup on file for a standard one.
 *
 * ALL of the behaviour now lives in the shared production primitive
 * (`@/components/production/ProductionPreviewTile`): the source chain, the
 * tier-aware captions, the skeleton, the fallback walk, the fixed footprint.
 * Catching, Print, Pressing and Film Print render the same tiles, so the
 * "never claim a side you cannot prove" rules are enforced in one place rather
 * than re-derived per station.
 *
 * What is left here is layout and load order: the packing checklist wants
 * the 58px checklist tile, painting a small copy first while the full-size is
 * fetched in the background, and the page builds its order-wide lightbox from
 * this same builder so a click opens the exact image the packer is pointing at.
 */
import { useMemo } from 'react';
import {
  ProductionPreviewStrip,
  buildFinishedGarmentPreviewEntries,
  thumbnailFirstChain,
  type ProductionPreviewEntry,
  type ProductionPreviewFacts,
  type ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';

/** 2x the widest checklist frame (116px), so one size serves both shapes. */
export const PACKING_THUMB_PX = 232;

export function buildPackingPreviewEntries(item: ProductionPreviewFacts): ProductionPreviewEntry[] {
  return buildFinishedGarmentPreviewEntries(item).map((entry) => ({
    ...entry,
    sources: thumbnailFirstChain(entry.sources, PACKING_THUMB_PX),
  }));
}

interface Props {
  item: ProductionPreviewFacts;
  /**
   * Opens the order-wide lightbox at this tile's image. `sourceIndex` is the
   * position in the entry's chain that ACTUALLY rendered, so the lightbox
   * opens on the image the packer is pointing at rather than on a first
   * choice this tile has already watched fail.
   */
  onOpenPreview?: (kind: ProductionPreviewKind, sourceIndex: number) => void;
  /** Alt text for the tiles (the product name). */
  productName: string;
}

export function PackingPreviewThumbnails({ item, onOpenPreview, productName }: Props) {
  const entries = useMemo(() => buildPackingPreviewEntries(item), [item]);
  return (
    <ProductionPreviewStrip
      entries={entries}
      subject={productName}
      variant="checklist"
      // A custom's one preview holds both sides in a single image, so it gets
      // the 2:1 frame; a standard's side tiles stay square and equal, and so
      // do a custom's Front and Back mockups when the client sends a back.
      frameShape={item.isCustomProduct && !item.customBackPreviewUrl ? 'wide' : 'square'}
      onOpen={onOpenPreview}
    />
  );
}
